import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const NOT_ALPHANUMERIC = /[^A-Za-z0-9]/g;

let nextId = 0;

function normalize(value: string): string {
  return value.replace(NOT_ALPHANUMERIC, '').toUpperCase();
}

@Component({
  selector: 'aav-code-input',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CodeInputComponent), multi: true },
  ],
  host: { class: 'block' },
  template: `
    <div class="flex items-center justify-center gap-2" role="group" [attr.aria-label]="ariaLabel">
      <input
        *ngFor="let digit of digits; let i = index; trackBy: trackByIndex"
        #box
        [id]="inputId + '-' + i"
        type="text"
        inputmode="text"
        autocapitalize="characters"
        autocomplete="one-time-code"
        maxlength="1"
        [value]="digit"
        [disabled]="disabled"
        [attr.aria-label]="digitLabel(i)"
        [attr.aria-invalid]="invalid"
        [class]="boxClasses"
        (input)="onInput($event, i)"
        (keydown)="onKeyDown($event, i)"
        (paste)="onPaste($event)"
        (focus)="onFocus($event)"
        (blur)="onTouched()"
      />
    </div>
  `,
})
export class CodeInputComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @Input() length = 6;
  @Input() disabled = false;
  @Input() invalid = false;
  @Input() ariaLabel = '';
  @Input() autoFocus = false;

  @Output() completed = new EventEmitter<string>();

  @ViewChildren('box') private boxes?: QueryList<ElementRef<HTMLInputElement>>;

  readonly inputId = `aav-code-input-${nextId++}`;

  digits: string[] = [];

  private onChange: (value: string) => void = () => undefined;
  onTouched: () => void = () => undefined;

  ngOnInit(): void {
    if (this.digits.length !== this.length) {
      this.digits = this.buildDigits(this.digits.join(''));
    }
  }

  ngAfterViewInit(): void {
    if (this.autoFocus && !this.disabled) {
      this.focusBox(0);
    }
  }

  get boxClasses(): string {
    const base =
      'h-12 w-11 rounded-aav-sm bg-transparent text-center text-aav-title-small-strong ' +
      'text-aav-on-surface outline-none ring-inset transition duration-150 ' +
      'disabled:cursor-not-allowed disabled:opacity-40';

    const state = this.invalid
      ? 'ring-2 ring-aav-error'
      : 'ring-1 ring-aav-outline-variant hover:ring-aav-on-surface-variant/40 focus:ring-2 focus:ring-aav-primary focus:shadow-aav-focus';

    return `${base} ${state}`;
  }

  trackByIndex(index: number): number {
    return index;
  }

  digitLabel(index: number): string {
    return `${index + 1}/${this.length}`;
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digit = normalize(input.value).slice(-1);

    input.value = digit;
    this.setDigit(index, digit);

    if (digit) {
      this.focusBox(index + 1);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      event.preventDefault();
      this.setDigit(index - 1, '');
      this.focusBox(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusBox(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusBox(index + 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pasted = normalize(event.clipboardData?.getData('text') ?? '').slice(0, this.length);

    if (!pasted) {
      return;
    }

    this.digits = this.buildDigits(pasted);
    this.emitValue();
    this.focusBox(Math.min(pasted.length, this.length - 1));
  }

  onFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  writeValue(value: string | null): void {
    this.digits = this.buildDigits(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private setDigit(index: number, digit: string): void {
    this.digits = this.digits.map((current, i) => (i === index ? digit : current));
    this.emitValue();
  }

  private emitValue(): void {
    const value = this.digits.join('');

    this.onChange(value);

    if (value.length === this.length) {
      this.completed.emit(value);
    }
  }

  private buildDigits(value: string): string[] {
    return Array.from({ length: this.length }, (_, i) => value[i] ?? '');
  }

  private focusBox(index: number): void {
    this.boxes?.get(index)?.nativeElement.focus();
  }
}
